import { motion } from 'framer-motion';
import {
  Trophy, Sparkles, TrendingUp, TrendingDown, Target, AlertTriangle,
  ShieldCheck, ShieldAlert, Gift, CheckCircle2, Circle, Flag,
} from 'lucide-react';
import { ScoreRing } from './ScoreRing';
import {
  C, fontSerif, fontMono, bandFor, kpiTheme, fmtNum, currencySymbol,
} from './utils';

const ICONS = { Trophy, Sparkles, TrendingUp, Target, AlertTriangle };

const Card = ({ children, className = '', delay = 0, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
    className={`rounded-2xl border bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(139,30,63,0.18)] ${className}`}
    style={{ borderColor: C.border }}
    data-testid={testId}
  >
    {children}
  </motion.div>
);

const KpiCard = ({ m, delay }) => {
  const theme = kpiTheme(m.type);
  const score = m.score;
  const gated = m.gate != null;
  const gateFailed = m.gate_failed;

  let detail = null;
  if (m.type === 'OUTPUT_COUNT') {
    detail = (
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold" style={{ fontFamily: fontMono, color: C.text }}>{fmtNum(m.actual)}</span>
        <span className="text-sm text-slate-400">/ {fmtNum(m.effective_target)} target</span>
      </div>
    );
  } else if (m.type === 'DAILY_ADHERENCE' || m.type === 'TEAM_DAILY_ADHERENCE') {
    detail = (
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold" style={{ fontFamily: fontMono, color: C.text }}>
          {m.actual_pct == null ? '—' : `${fmtNum(m.actual_pct, 1)}%`}
        </span>
        <span className="text-sm text-slate-400">of {fmtNum(m.target)}% goal</span>
      </div>
    );
  } else if (m.type === 'RATE' || m.type === 'SLA_RATE') {
    detail = (
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold" style={{ fontFamily: fontMono, color: C.text }}>
          {m.actual_pct == null ? '—' : `${fmtNum(m.actual_pct, 1)}%`}
        </span>
        <span className="text-sm text-slate-400">{fmtNum(m.successes)}/{fmtNum(m.eligible)} · goal {fmtNum(m.target)}%</span>
      </div>
    );
  } else if (m.type === 'MANUAL_ACCEPTED_COUNT') {
    detail = (
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold" style={{ fontFamily: fontMono, color: C.text }}>{fmtNum(m.actual)}</span>
        <span className="text-sm text-slate-400">/ {fmtNum(m.monthly_target)} this month</span>
      </div>
    );
  }

  return (
    <Card delay={delay} testId={`kpi-card-${m.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${theme.badge}`}>
            {theme.label}
          </span>
          <h4 className="mt-2 truncate text-sm font-semibold" style={{ color: C.text }}>{m.label}</h4>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: fontMono, color: theme.color }}>
            {score == null ? '—' : fmtNum(score, 1)}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400">score</div>
        </div>
      </div>

      <div className="mt-3">{detail}</div>

      <div className="mt-3 h-2 w-full overflow-hidden rounded-full" style={{ background: theme.tint }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.min(score || 0, 100)}%`, background: theme.color }}
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-400">Weight {m.weight}%</span>
        {gated && (
          <span className={`inline-flex items-center gap-1 font-medium ${gateFailed ? 'text-rose-600' : 'text-emerald-600'}`}>
            {gateFailed ? <ShieldAlert className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
            Gate {fmtNum(m.gate)}%
          </span>
        )}
      </div>
    </Card>
  );
};

export const MyPerformanceView = ({ report }) => {
  if (!report) return null;
  const band = bandFor(report.score_band, report.reward_status);
  const BandIcon = ICONS[band.icon] || Target;
  const unlocked = report.reward_status === 'UNLOCKED';
  const reward = report.reward || {};
  const sym = currencySymbol(reward.currency);
  const emp = report.employee || {};
  const today = report.today || {};

  const outputMetric = (report.metrics || []).find((m) => m.type === 'OUTPUT_COUNT');
  let pace = null;
  if (outputMetric && outputMetric.actual != null && outputMetric.expected != null && report.closed_eligible_days > 0) {
    pace = Math.round(outputMetric.actual - outputMetric.expected);
  }

  const gates = (report.metrics || []).filter((m) => m.gate != null);
  const todayPct = today.target > 0 ? Math.min((today.completed / today.target) * 100, 100) : 0;

  return (
    <div className="space-y-6" data-testid="my-performance-view">
      {/* Hero score */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1 flex flex-col items-center justify-center text-center" testId="hero-score-card">
          {unlocked && (
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900"
            >
              <Sparkles className="h-3.5 w-3.5" /> Reward unlocked!
            </motion.div>
          )}
          <ScoreRing
            score={report.overall_score}
            threshold={report.threshold}
            color={band.ring}
            testId="hero-score-ring"
            label={report.provisional ? 'provisional' : null}
          />
          <span className={`mt-4 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold ${band.badge}`} data-testid="reward-status-pill">
            <BandIcon className="h-4 w-4" />
            {report.reward_status === 'DISQUALIFIED' ? 'Gate Blocked' : band.label}
          </span>
          {report.blocking_reason && (
            <p className="mt-3 text-sm text-slate-500">{report.blocking_reason}</p>
          )}
        </Card>

        <div className="lg:col-span-2 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {/* Reward distance */}
          <Card testId="reward-distance-card" delay={0.05}>
            <div className="flex items-center gap-2 text-slate-500">
              <Gift className="h-4 w-4" style={{ color: C.gold }} />
              <span className="text-xs uppercase tracking-wider">The prize</span>
            </div>
            <div className="mt-2 text-2xl font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>
              {sym}{reward.amount ? reward.amount.toLocaleString('en-IN') : '—'} {reward.name}
            </div>
            <div className="mt-3 text-sm text-slate-500">
              {report.distance_to_threshold == null ? (
                'Score not started yet'
              ) : report.distance_to_threshold <= 0 ? (
                <span className="font-semibold text-emerald-600">You&apos;ve cleared the {report.threshold} threshold 🎉</span>
              ) : (
                <>
                  <span className="text-lg font-bold" style={{ fontFamily: fontMono, color: C.burgundy }}>
                    {fmtNum(report.distance_to_threshold, 1)}
                  </span>{' '}
                  points to reach {report.threshold}
                </>
              )}
            </div>
          </Card>

          {/* Projected month end */}
          <Card testId="projected-score-card" delay={0.1}>
            <div className="flex items-center gap-2 text-slate-500">
              <TrendingUp className="h-4 w-4 text-indigo-500" />
              <span className="text-xs uppercase tracking-wider">Projected month-end</span>
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="text-4xl font-bold tabular-nums" style={{ fontFamily: fontMono, color: C.text }}>
                {report.projected_month_end == null ? '—' : fmtNum(report.projected_month_end, 1)}
              </span>
              <span className="text-sm text-slate-400">/100</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              Based on your pace over {report.closed_eligible_days} of {report.total_eligible_days} working days.
            </p>
          </Card>

          {/* Today's target */}
          <Card testId="today-target-card" delay={0.15}>
            <div className="flex items-center gap-2 text-slate-500">
              <Target className="h-4 w-4" style={{ color: C.burgundy }} />
              <span className="text-xs uppercase tracking-wider">Today&apos;s target</span>
            </div>
            {today.is_working_day ? (
              <>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold tabular-nums" style={{ fontFamily: fontMono, color: C.text }}>
                    {fmtNum(today.completed)}
                  </span>
                  <span className="text-lg text-slate-400">/ {fmtNum(today.target)} {emp.output_unit_label}</span>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full" style={{ background: C.muted }}>
                  <div className="h-full rounded-full" style={{ width: `${todayPct}%`, background: C.burgundy }} />
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  {today.remaining > 0 ? `${fmtNum(today.remaining)} to go today` : 'Daily target met ✓'}
                  {today.closed && ' · day closed'}
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm text-slate-500">Not a working day — enjoy the break.</p>
            )}
          </Card>

          {/* Monthly pace */}
          <Card testId="monthly-pace-badge" delay={0.2}>
            <div className="flex items-center gap-2 text-slate-500">
              {pace != null && pace < 0 ? <TrendingDown className="h-4 w-4 text-rose-500" /> : <TrendingUp className="h-4 w-4 text-teal-500" />}
              <span className="text-xs uppercase tracking-wider">Monthly output pace</span>
            </div>
            {pace == null ? (
              <p className="mt-3 text-sm text-slate-500">No closed days yet.</p>
            ) : (
              <>
                <div className="mt-2 flex items-baseline gap-1.5">
                  <span
                    className="text-3xl font-bold tabular-nums"
                    style={{ fontFamily: fontMono, color: pace >= 0 ? C.teal : C.rose }}
                  >
                    {pace >= 0 ? '+' : ''}{fmtNum(pace)}
                  </span>
                  <span className="text-sm text-slate-400">{emp.output_unit_label} {pace >= 0 ? 'ahead' : 'behind'}</span>
                </div>
                <p className="mt-2 text-sm text-slate-500">
                  {fmtNum(outputMetric.actual)} done vs {fmtNum(outputMetric.expected)} expected so far.
                </p>
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Quality gates */}
      {gates.length > 0 && (
        <Card testId="quality-gates-card">
          <h3 className="text-lg font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>Quality Gates</h3>
          <p className="text-sm text-slate-500">These must stay above their minimum or the reward is blocked.</p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {gates.map((g) => (
              <div
                key={g.key}
                className={`flex items-center justify-between rounded-xl border px-4 py-3 ${g.gate_failed ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`}
                data-testid={`gate-${g.key}`}
              >
                <div className="flex items-center gap-2">
                  {g.gate_failed ? <Circle className="h-4 w-4 text-rose-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  <span className="text-sm font-medium" style={{ color: C.text }}>{g.label}</span>
                </div>
                <span className="text-sm font-bold tabular-nums" style={{ fontFamily: fontMono, color: g.gate_failed ? C.rose : C.teal }}>
                  {g.actual_pct == null ? '—' : `${fmtNum(g.actual_pct, 1)}%`}
                  <span className="ml-1 text-xs font-normal text-slate-400">min {fmtNum(g.gate)}%</span>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* KPI breakdown */}
      <div>
        <h3 className="mb-4 text-lg font-semibold" style={{ fontFamily: fontSerif, color: C.text }}>KPI Breakdown</h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="kpi-list-container">
          {(report.metrics || []).map((m, i) => (
            <KpiCard key={m.key} m={m} delay={i * 0.05} />
          ))}
        </div>
      </div>
    </div>
  );
};
